/**
 * Runtime theme engine (Phase 1).
 *
 * The app's colour tokens are Tailwind v4 `@theme` variables (see
 * `styles/globals.css`); every utility (`bg-bg`, `text-brand`, `border-border`,
 * …) resolves `var(--color-*)`. Setting those vars inline on `:root` overrides
 * the build-time values, so a theme change recolours the whole app instantly
 * with no rebuild.
 *
 * Phase 1 ships named presets plus a `custom` palette derived from a
 * user-picked background + accent. Prefs are persisted locally (localStorage),
 * mirroring the `useGameSelection` + `storage.ts` pattern. Phase 3 will sync to
 * the customization-backend and unlock themes via gamification / AI.
 */

import { mix, relativeLuminance } from './color';
import { storage, StorageKey } from './storage';
import { setTelegramChrome } from './telegram';

/** The CSS variables the engine drives. */
export type TokenName =
  | '--color-bg'
  | '--color-section'
  | '--color-bg-secondary'
  | '--color-bg-tertiary'
  | '--color-fg'
  | '--color-fg-muted'
  | '--color-border'
  | '--color-brand'
  | '--color-brand-fg';

export type Palette = Record<TokenName, string>;

export interface AppearancePrefs {
  /** Preset id, or `'custom'` to use customBg/customAccent. */
  presetId: string;
  customBg?: string;
  customAccent?: string;
}

export interface ThemePreset {
  id: string;
  /** Display name — kept brand-literal (not translated). */
  name: string;
  palette: Palette;
}

const CYAN = '#00e7df';
const LIGHT_FG = '#e0e3e2';
const DARK_FG = '#0b0f0f';

/**
 * Build a coherent dark palette from a base background + accent colour.
 * Surfaces/border are lifted toward the foreground; foreground and
 * text-on-accent flip by luminance so any user-picked colour stays legible.
 */
export function derivePalette(bg: string, accent: string): Palette {
  const fg = relativeLuminance(bg) > 0.55 ? DARK_FG : LIGHT_FG;
  const accentFg = relativeLuminance(accent) > 0.6 ? '#06201f' : '#ffffff';
  return {
    '--color-bg': bg,
    '--color-section': bg,
    '--color-bg-secondary': mix(bg, fg, 0.08),
    '--color-bg-tertiary': mix(bg, fg, 0.16),
    '--color-fg': fg,
    '--color-fg-muted': mix(fg, bg, 0.4),
    '--color-border': mix(bg, fg, 0.22),
    '--color-brand': accent,
    '--color-brand-fg': accentFg,
  };
}

export const PRESETS: ThemePreset[] = [
  {
    // The default: pure-black base, QLASH cyan accent, subtly-lifted cards.
    id: 'qlash-black',
    name: 'QLASH Black',
    palette: {
      '--color-bg': '#000000',
      '--color-section': '#000000',
      '--color-bg-secondary': '#191c1c',
      '--color-bg-tertiary': '#282828',
      '--color-fg': '#e0e3e2',
      '--color-fg-muted': '#9a9a9a',
      '--color-border': '#2f3434',
      '--color-brand': CYAN,
      '--color-brand-fg': '#003737',
    },
  },
  {
    // The previous warm off-black look, for users who prefer it.
    id: 'qlash-classic',
    name: 'QLASH Classic',
    palette: {
      '--color-bg': '#101010',
      '--color-section': '#101010',
      '--color-bg-secondary': '#191c1c',
      '--color-bg-tertiary': '#282828',
      '--color-fg': '#e0e3e2',
      '--color-fg-muted': '#9a9a9a',
      '--color-border': '#2f3434',
      '--color-brand': CYAN,
      '--color-brand-fg': '#003737',
    },
  },
  { id: 'midnight', name: 'Midnight', palette: derivePalette('#05070f', '#5b8cff') },
  { id: 'crimson', name: 'Crimson', palette: derivePalette('#000000', '#ff3b46') },
  { id: 'violet', name: 'Violet', palette: derivePalette('#070512', '#a855f7') },
];

export const DEFAULT_PREFS: AppearancePrefs = { presetId: 'qlash-black' };
const DEFAULT_PRESET = PRESETS[0];
const PRESET_BY_ID = new Map(PRESETS.map((p) => [p.id, p]));

/** Resolve prefs → a concrete palette. */
export function resolvePalette(prefs: AppearancePrefs): Palette {
  if (prefs.presetId === 'custom') {
    return derivePalette(
      prefs.customBg ?? DEFAULT_PRESET.palette['--color-bg'],
      prefs.customAccent ?? CYAN,
    );
  }
  return (PRESET_BY_ID.get(prefs.presetId) ?? DEFAULT_PRESET).palette;
}

/** Apply a palette to the document + sync the native Telegram chrome. */
export function applyPalette(palette: Palette): void {
  const root = document.documentElement;
  (Object.keys(palette) as TokenName[]).forEach((name) => {
    root.style.setProperty(name, palette[name]);
  });
  setTelegramChrome(palette['--color-bg']);
}

/** Read persisted prefs (localStorage). Falls back to the default. */
export function loadStoredAppearance(): AppearancePrefs {
  const raw = storage.get(StorageKey.Appearance);
  if (!raw) return DEFAULT_PREFS;
  try {
    const parsed = JSON.parse(raw) as Partial<AppearancePrefs>;
    if (parsed && typeof parsed.presetId === 'string') {
      return {
        presetId: parsed.presetId,
        customBg: typeof parsed.customBg === 'string' ? parsed.customBg : undefined,
        customAccent:
          typeof parsed.customAccent === 'string' ? parsed.customAccent : undefined,
      };
    }
  } catch {
    /* corrupt value — fall through to default */
  }
  return DEFAULT_PREFS;
}

/** Persist prefs (localStorage). */
export function saveAppearance(prefs: AppearancePrefs): void {
  storage.set(StorageKey.Appearance, JSON.stringify(prefs));
}
